import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dropdown,
  Empty,
  Flex,
  Input,
  Modal,
  Popover,
  Space,
  Spin,
  Table,
  Tag,
  theme,
  Tooltip,
  Typography,
  message,
  type TableColumnsType,
} from "antd";
import {
  AppstoreOutlined,
  CheckCircleFilled,
  DownloadOutlined,
  MoreOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { PaginationTotal } from "@/components/pagination-total";
import { OPCIONES_POR_PAGINA } from "@/config/paginacion";
import { useTranslate } from "@refinedev/core";
import { axiosInstance } from "@/utilities";
import {
  API_URL_PIM_ATTRIBUTE_SETS,
  API_URL_PIM_PRODUCTS,
} from "@/services/XCONS_ENV";
import { CONJUNTOS_DEL_ALTA, etiquetaConjunto } from "./conjuntos";
import { useColumnOrder } from "@/pages/quotes/utils/useColumnOrder";
import { filtrosDelListado, useCatalogList } from "./useCatalogList";
import { CatalogAdminsDrawer } from "./CatalogAdminsDrawer";
import { CatalogColumnHeaderCell } from "./CatalogColumnHeaderCell";
import { CatalogFilterTags, CatalogFiltersButton } from "./CatalogFilters";
import { BulkActions } from "./BulkActions";
import { useSeccionDeAtributos } from "./attributes/seccion-de-atributos";
import { puedeMarcarse } from "./acciones-en-lote";
import { ESTADOS, estadoDelHit, etiquetaEstado, esEstadoPublicado } from "./estados";
import { esBorrador } from "./types";
import { totalPaginable } from "./tope-del-indice";
import { esVistaSinPublicar, filasDeLaPagina } from "./filas-de-la-pagina";
import type { CatalogRow } from "./types";

/** Hasta cuántos productos sale un export (xcons-docs#477). El API exige el mismo. */
const MAX_EXPORT = 300;

/** El orden base de las columnas, tal como se declaran más abajo — es lo que
 * `useColumnOrder` persiste y reordena. */
const CATALOG_COLUMN_KEYS = [
  "sku",
  "name",
  "manufacturer",
  "attributeSet",
  "state",
  "completeness",
];

const { Text, Title } = Typography;

/**
 * **Los anchos de las columnas.** SKU, Nombre y Marca se reparten lo que la
 * pantalla deja libre; el resto mide lo que su título o su contenido más ancho, y la de
 * los tres puntitos, lo que el botón. El `width` de las responsivas es su mínimo.
 *
 * Se calcula acá porque la tabla sola no lo hace: con `scroll.y` pasa a layout fijo, y
 * ahí el navegador reparte el sobrante entre **todas** las columnas, así que ninguna queda
 * del ancho que se le pidió.
 */
const RESPONSIVAS = new Set(["sku", "name", "manufacturer"]);
const ANCHO_MENU = 40;
/** La columna de los checkboxes de la selección: la fija antd. */
const ANCHO_SELECCION = 32;
/** El padding de una celda de la tabla `small`: 8 de cada lado. */
const PADDING_DE_CELDA = 16;
/** Lo que el título ocupa además del texto: el asa de arrastre (14 + 4) y el padding. */
const EXTRA_DEL_TITULO = 18 + PADDING_DE_CELDA;
/** Lo que un Tag ocupa además del texto: padding (7 + 7), borde (1 + 1) y margen (8). */
const EXTRA_DEL_TAG = 16 + 8;
/** El canal de la barra de scroll vertical, que la tabla le descuenta al ancho. */
const CANAL_DEL_SCROLL = 16;

let lienzo: CanvasRenderingContext2D | null = null;
const anchoDeTexto = (texto: string, fuente: string) => {
  lienzo ??= document.createElement("canvas").getContext("2d");
  if (!lienzo) return texto.length * 8;
  lienzo.font = fuente;
  return lienzo.measureText(texto).width;
};

/**
 * Listado del catálogo, con las **tres vistas segregadas**.
 *
 * No es un listado con un filtro de estado: cada vista sale de una fuente distinta
 * —Publicados del índice main, Borradores y Pendientes de la base del admin— y esas
 * fuentes tienen capacidades de filtro distintas. Paginar su unión obligaría a
 * materializar las dos.
 *
 * **Costo asumido y visible para el usuario:** la búsqueda de las dos vistas de borradores
 * es más simple. Un typo encuentra el publicado y no encuentra el borrador, y por eso el
 * placeholder del buscador cambia según la vista.
 */
export const CatalogList: React.FC = () => {
  const navigate = useNavigate();
  const t = useTranslate();
  const { token } = theme.useToken();
  const [ocultas, setOcultas] = useState<string[]>([]);
  const contenedor = useRef<HTMLDivElement>(null);
  const [anchoDisponible, setAnchoDisponible] = useState(0);
  useEffect(() => {
    const el = contenedor.current;
    if (!el) return;
    const observador = new ResizeObserver(([entrada]) =>
      setAnchoDisponible(entrada.contentRect.width),
    );
    observador.observe(el);
    return () => observador.disconnect();
  }, []);

  const { order: ordenColumnas, reorder: reordenarColumnas } = useColumnOrder({
    storageKey: "xcons:catalog:columnOrder:v1",
    togglableKeys: CATALOG_COLUMN_KEYS,
  });
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const onDragEndColumnas = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      reordenarColumnas(String(active.id), String(over.id));
    }
  };

  const puedeVerAtributos = useSeccionDeAtributos();
  const {
    puedeEscribirTodo,
    puedeCrear,
    esAdminDeCatalogo,
    loading,
    primeraCarga,
    error,
    hits,
    drafts,
    total,
    page,
    pageSize,
    filters,
    facetValues,
    setPage,
    setPageSize,
    applyFilters,
  } = useCatalogList();

  /**
   * **El alto de las filas, lo que queda de pantalla** (xcons-docs#499). Arriba de las filas
   * el Catálogo tiene más que las otras tablas —buscador, filtros aplicados, cantidad y la
   * barra de seleccionados—, y con el alto fijo de todas el paginador quedaba fuera de la
   * pantalla. Se vuelve a medir cuando cambia lo de arriba o el tamaño de la ventana.
   */
  const [altoFilas, setAltoFilas] = useState<number | null>(null);
  useEffect(() => {
    const el = contenedor.current;
    if (!el) return;
    const medir = () => {
      const filas = el.querySelector<HTMLElement>(".ant-table-body");
      if (!filas) return;
      const paginador = el.querySelector<HTMLElement>(".ant-table-pagination");
      // El paginador con sus márgenes (16 y 16) y el margen de abajo de la página (24).
      const pie = (paginador?.offsetHeight ?? 32) + 32 + 24;
      setAltoFilas(
        Math.max(240, Math.floor(window.innerHeight - filas.getBoundingClientRect().top - pie)),
      );
    };
    medir();
    const observador = new ResizeObserver(medir);
    const titulo = el.querySelector(".ant-table-title");
    if (titulo) observador.observe(titulo);
    window.addEventListener("resize", medir);
    return () => {
      observador.disconnect();
      window.removeEventListener("resize", medir);
    };
  }, [loading]);

  /**
   * La selección para las masivas (xcons-docs#433). Se guardan las FILAS y no las claves: el
   * confirm tiene que decir cuántos de cada estado abarca, y eso sale de la fila.
   */
  const [seleccion, setSeleccion] = useState<CatalogRow[]>([]);
  /**
   * Cambiar de filtro deja la selección vacía: las filas marcadas pueden ya no estar. Cambiar
   * de página no (xcons-docs#499): se eligen 20 en una y 30 en otra, y el lote va con las 50.
   */
  const filtrar: typeof applyFilters = (next) => {
    setSeleccion([]);
    applyFilters(next);
  };

  /**
   * **El conjunto se elige antes de abrir el form**, como el Store en Nuevo presupuesto:
   * es lo que decide qué form se muestra, así que adentro ya no se cambia.
   */
  const [todosLosConjuntos, setTodosLosConjuntos] = useState<
    Array<{ attributeSetId: number; name: string }>
  >([]);
  useEffect(() => {
    axiosInstance
      .get<Array<{ attributeSetId: number; name: string }>>(
        API_URL_PIM_ATTRIBUTE_SETS,
      )
      .then(({ data }) => setTodosLosConjuntos(data ?? []))
      .catch(() => setTodosLosConjuntos([]));
  }, []);
  const conjuntos = useMemo(
    () =>
      puedeCrear
        ? todosLosConjuntos.filter((c) => CONJUNTOS_DEL_ALTA.includes(c.name))
        : [],
    [puedeCrear, todosLosConjuntos],
  );
  /**
   * El nombre del conjunto por id, para las filas sin publicar (xcons-docs#463): un borrador
   * trae `attributeSetId` y no el nombre, que sólo lo pone el índice.
   */
  const nombreDeConjunto = useMemo(
    () => new Map(todosLosConjuntos.map((c) => [c.attributeSetId, c.name])),
    [todosLosConjuntos],
  );

  /**
   * **Una sola tabla para las dos fuentes.** Las columnas que sólo una sabe contestar
   * —completitud y conjunto, del índice— muestran un guion
   * en las filas de la otra: esconderlas según la fila sería una tabla que cambia de forma
   * mientras se la mira.
   */
  const columnas: TableColumnsType<CatalogRow> = useMemo(
    () => [
      {
        key: "sku",
        dataIndex: "sku",
        title: "SKU",
        width: 190,
        render: (sku: string) => <Text strong>{sku}</Text>,
      },
      {
        key: "name",
        dataIndex: "name",
        title: "Nombre",
        // Ancho acotado y el nombre en hasta dos líneas: con el `scroll.x` en
        // `max-content`, una columna sin ancho se estira hasta el nombre más largo
        // (xcons-docs#390).
        width: 300,
        render: (v: string | null) =>
          v ? (
            <Text
              title={v}
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {v}
            </Text>
          ) : (
            <Text type="secondary">(sin nombre)</Text>
          ),
      },
      {
        key: "manufacturer",
        dataIndex: "manufacturer",
        title: "Marca",
        width: 150,
        render: (v: string | false | null) =>
          v || <Text type="secondary">—</Text>,
      },
      {
        key: "attributeSet",
        dataIndex: "attributeSet",
        title: "Conjunto",
        render: (v: string | false | undefined, row: CatalogRow) => {
          const nombre =
            v ||
            (esBorrador(row) && row.attributeSetId
              ? nombreDeConjunto.get(row.attributeSetId)
              : undefined);
          return nombre ? (
            etiquetaConjunto(nombre)
          ) : (
            <Text type="secondary">—</Text>
          );
        },
      },
      {
        key: "state",
        title: "Estado",
        // El filtro de Estado vive en el side panel de Filtros (xcons-docs#366), no en la
        // columna: esta sigue mostrando el Tag, pero ya no ofrece su propio dropdown.
        render: (_: unknown, row: CatalogRow) => {
          // Un publicado sale por su `state` (#380): con `status` un archivado se
          // mostraba como Pausado.
          const estado = esBorrador(row)
            ? ESTADOS[row.state]
            : ESTADOS[estadoDelHit(row)];
          return (
            <Tag color={estado.color} style={estado.style}>
              {estado.label}
            </Tag>
          );
        },
      },
      {
        key: "completeness",
        title: "Completo",
        align: "center",
        // El porcentaje solo, sin barra; al 100% lleva el check.
        render: (_: unknown, row: CatalogRow) =>
          esBorrador(row) ? (
            <Text type="secondary">—</Text>
          ) : (
            <Tooltip
              title={
                row.completenessMissing?.length
                  ? `Falta: ${row.completenessMissing.join(", ")}`
                  : "Cumple CON-P001"
              }
            >
              <Space size={4}>
                <Text>{row.completeness ?? 0}%</Text>
                {row.completeness === 100 && (
                  <CheckCircleFilled style={{ color: token.colorSuccess }} />
                )}
              </Space>
            </Tooltip>
          ),
      },
    ],
    [token, nombreDeConjunto],
  );

  /**
   * Lo que se muestra, en orden: **primero lo que no se publicó**.
   *
   * Son dos fuentes que no se pueden paginar juntas —una es el índice y la otra SQL—, así
   * que los no publicados, que son cientos y no cientos de miles, van enteros arriba y la
   * paginación sigue siendo la de los publicados. Ponerlos abajo los escondería en la
   * página 12, que es donde nadie mira lo que dejó a medias.
   */
  /**
   * **Lo que está en revisión va siempre primero**: es trabajo que espera a alguien, y es
   * lo que reemplaza al banner de la cola. Después los borradores, y después los
   * publicados con el orden de la tabla. Al publicarse, un producto deja de ser fila de
   * `drafts` y cae solo en su lugar entre los `hits`.
   */
  /** Ver `filasDeLaPagina`: en *En revisión* o *Borrador* se pagina en memoria (#632). */
  const soloSinPublicar = esVistaSinPublicar(filters.estado);

  const filas: CatalogRow[] = useMemo(
    () => filasDeLaPagina({ drafts, hits, page, pageSize, soloSinPublicar }),
    [drafts, hits, page, pageSize, soloSinPublicar],
  );

  /**
   * **Exportar en el formato del importador** (xcons-docs#467): lo que sale se edita en Excel
   * y vuelve a entrar por Importar sin mapear nada. Lo arma el API —columnas del form,
   * valores por etiqueta, categorías con la raíz— para que export e import salgan de la misma
   * definición.
   *
   * Lo seleccionado, si hay selección; si no, todo lo que filtra el listado. Los borradores
   * no se exportan: no son productos todavía, y el importador ya los arma desde una planilla.
   */
  const [exportando, setExportando] = useState(false);
  /**
   * **Máximo 300 por export** (xcons-docs#477): más que eso no se exporta nada, y se avisa
   * antes de pedirlo. El tope lo exige también el API (`EXPORT_TOO_MANY`), que es el que
   * responde si el total del listado cambió entre que se cargó y se exportó.
   */
  const avisarTope = (cuantos: number) =>
    Modal.warning({
      title: `Se exportan hasta ${MAX_EXPORT} productos`,
      content: `Son ${cuantos.toLocaleString("es-AR")}. Filtrá el listado o seleccioná menos para exportar.`,
      okText: "Entendido",
    });
  const exportar = async () => {
    const elegidos = seleccion
      .filter((row) => !esBorrador(row))
      .map((row) => Number((row as { magentoProductId?: number }).magentoProductId))
      .filter(Boolean);
    if (seleccion.length && !elegidos.length) {
      message.info("Los borradores no se exportan: elegí productos publicados");
      return;
    }
    // Filtrado por Borrador o En revisión, el listado muestra borradores, que no se exportan:
    // sin esto el export ignoraba ese filtro y bajaba todos los publicados.
    if (!elegidos.length && filters.estado && !esEstadoPublicado(filters.estado)) {
      message.info("Los borradores no se exportan: filtrá por un estado publicado");
      return;
    }
    const aExportar = elegidos.length || total;
    if (aExportar > MAX_EXPORT) {
      avisarTope(aExportar);
      return;
    }
    setExportando(true);
    try {
      const { data } = await axiosInstance.post<{
        columns: Array<{ key: string; label: string }>;
        rows: Array<Record<string, string>>;
        total: number;
        truncated: boolean;
      }>(
        `${API_URL_PIM_PRODUCTS}/export`,
        elegidos.length ? { productIds: elegidos } : filtrosDelListado(filters),
      );
      if (!data.rows.length) {
        message.info("No hay productos para exportar con estos filtros");
        return;
      }
      // Encabezados en el orden del form, y una celda por columna aunque esté vacía: así el
      // archivo tiene siempre la misma forma y el importador lo reconoce entero.
      const hoja = XLSX.utils.aoa_to_sheet([
        data.columns.map((c) => c.label),
        ...data.rows.map((fila) => data.columns.map((c) => fila[c.key] ?? "")),
      ]);
      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, "Productos");
      const hoy = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(libro, `catalogo-${hoy}.xlsx`);
      if (data.truncated) {
        message.warning(
          `Se exportaron ${data.rows.length.toLocaleString("es-AR")} de ${data.total.toLocaleString("es-AR")} productos. Filtrá el listado para exportar el resto.`,
          8,
        );
      } else {
        message.success(`${data.rows.length.toLocaleString("es-AR")} productos exportados`);
      }
    } catch (e) {
      const data = (
        e as { response?: { data?: { code?: string; message?: string; total?: number } } }
      )?.response?.data;
      if (data?.code === "EXPORT_TOO_MANY" && data.total) {
        avisarTope(data.total);
        return;
      }
      message.error(data?.message ?? "No se pudo exportar");
    } finally {
      setExportando(false);
    }
  };

  const visibles = ordenColumnas
    .map((key) => columnas.find((c) => String(c.key) === key))
    .filter(
      (c): c is (typeof columnas)[number] =>
        !!c && !ocultas.includes(String(c.key)),
    );
  const fuenteDelTitulo = `${token.fontWeightStrong} ${token.fontSize}px ${token.fontFamily}`;
  const fuenteDeCelda = `${token.fontSize}px ${token.fontFamily}`;
  const fuenteDeTag = `${token.fontSizeSM}px ${token.fontFamily}`;
  /**
   * Lo más ancho que puede mostrar cada columna fija, además del título. Sale de todos los
   * valores posibles y no de los de la página, para que la columna no cambie de ancho al
   * paginar o filtrar: todos los estados, y todos los conjuntos que tiene el catálogo.
   */
  const contenidoMasAncho: Record<string, number> = {
    attributeSet:
      Math.max(
        0,
        ...facetValues("attributeSet").map((f) =>
          anchoDeTexto(etiquetaConjunto(f.value), fuenteDeCelda),
        ),
      ) + PADDING_DE_CELDA,
    state:
      Math.max(
        ...Object.values(ESTADOS).map((e) => anchoDeTexto(e.label, fuenteDeTag)),
      ) +
      EXTRA_DEL_TAG +
      PADDING_DE_CELDA,
  };
  const anchoFijo = (c: (typeof columnas)[number]) =>
    Math.ceil(
      Math.max(
        anchoDeTexto(String(c.title), fuenteDelTitulo) + EXTRA_DEL_TITULO,
        contenidoMasAncho[String(c.key)] ?? 0,
      ),
    );
  const minimoResponsivas = visibles
    .filter((c) => RESPONSIVAS.has(String(c.key)))
    .reduce((n, c) => n + Number(c.width ?? 0), 0);
  const sobrante = Math.max(
    0,
    anchoDisponible -
      CANAL_DEL_SCROLL -
      ANCHO_MENU -
      ANCHO_SELECCION -
      minimoResponsivas -
      visibles
        .filter((c) => !RESPONSIVAS.has(String(c.key)))
        .reduce((n, c) => n + anchoFijo(c), 0),
  );
  const estirar = minimoResponsivas ? 1 + sobrante / minimoResponsivas : 1;

  const columnasVisibles = [
    ...visibles.map((c) => ({
      ...c,
      width: RESPONSIVAS.has(String(c.key))
        ? Math.floor(Number(c.width ?? 0) * estirar)
        : anchoFijo(c),
      onHeaderCell: () => ({ columnKey: String(c.key) } as any),
    })),
    // El selector de columnas vive como una columna finita más, al final de la tabla
    // (xcons-docs#366, punto 8) — no es draggable ni se oculta: es el propio control.
    {
      key: "columnasMenu",
      width: 40,
      title: (
        // Checkbox por columna, el mismo selector que el de métricas del Dashboard
        // (`metrics-panel`, `CustomizationContent`).
        <Popover
          trigger="click"
          placement="bottomRight"
          content={
            <div style={{ width: 200 }} onClick={(e) => e.stopPropagation()}>
              {columnas.map((c) => {
                const key = String(c.key);
                const alternar = () =>
                  setOcultas((prev) =>
                    prev.includes(key)
                      ? prev.filter((k) => k !== key)
                      : [...prev, key],
                  );
                return (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "5px 4px",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                    onClick={alternar}
                  >
                    <Checkbox
                      checked={!ocultas.includes(key)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={alternar}
                    />
                    <Text
                      style={{
                        fontSize: 13,
                        flex: 1,
                        lineHeight: "20px",
                        userSelect: "none",
                      }}
                    >
                      {String(c.title)}
                    </Text>
                  </div>
                );
              })}
            </div>
          }
        >
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Popover>
      ),
      render: () => null,
    },
  ] as TableColumnsType<any>;

  /** Los empty states significan cosas distintas y por eso son varios. */
  const vacio = () => {
    // Una página vacía con total es una página que el índice no devuelve, no un catálogo
    // vacío (xcons-docs#517). Con el total topeado no se llega, salvo que el total cambie
    // estando parado en la última.
    if (total > 0) {
      return (
        <Empty description="No hay más productos en esta página: volvé a la anterior o filtrá" />
      );
    }
    if (filters.search) {
      return (
        <Empty description={`No hay resultados para "${filters.search}"`} />
      );
    }
    if (filters.estado) {
      return (
        <Empty
          description={`No hay productos en estado "${etiquetaEstado(
            filters.estado,
          )}"`}
        />
      );
    }
    return <Empty description="Todavía no hay productos en el catálogo" />;
  };

  // Hasta saber el nivel, un spinner: la pantalla no arranca sin los botones que después
  // aparecen (xcons-docs#487). Mismo spinner que la ficha.
  if (primeraCarga) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    );
  }

  return (
    <>
      <Flex align="center" justify="space-between" style={{ marginBottom: 24 }}>
        <Flex align="center" gap={8}>
          <AppstoreOutlined style={{ fontSize: 20 }} />
          <Title level={3} style={{ marginBottom: 0 }}>
            Catálogo
          </Title>
          {/* Cambia de vista. Atributos va con su permiso aparte, sí/no, que se da en la base
            y no depende del nivel del Catálogo (xcons-docs#478). Mismo patrón que
            Contactos/Grupos. */}
          {puedeVerAtributos && (
            <Dropdown
              trigger={["click"]}
              menu={{
                selectedKeys: ["catalog"],
                items: [
                  { key: "catalog", label: "Catálogo" },
                  { key: "attributes", label: "Atributos" },
                ],
                onClick: ({ key }) => {
                  if (key === "attributes") navigate("/catalog/attributes");
                },
              }}
            >
              <Button icon={<MoreOutlined />} />
            </Dropdown>
          )}
        </Flex>
        {/* Arriba a la derecha, por fuera de la tabla — mismo lugar que en Presupuestos y
          Pedidos, no en la barra de acciones del Card (xcons-docs#366, punto 11). */}
        {(puedeEscribirTodo || esAdminDeCatalogo) && <CatalogAdminsDrawer />}
      </Flex>
      {/* Sin Card: el look de tarjeta blanca lo dan los tokens de tema sobre el propio
        Table (xconsTheme.tsx) más el `title` de abajo, igual que Contactos/Presupuestos/
        Pedidos — es lo que deja a la tabla llegar sola a los bordes (xcons-docs#366). */}
      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEndColumnas}
      >
        <SortableContext
          items={columnasVisibles
            .filter((c) => c.key !== "columnasMenu")
            .map((c) => String(c.key))}
          strategy={horizontalListSortingStrategy}
        >
          <div
            ref={contenedor}
            className="tabla-al-alto-de-pantalla"
            style={
              altoFilas ? ({ "--alto-filas": `${altoFilas}px` } as React.CSSProperties) : undefined
            }
          >
            <Table
              // Las dos fuentes tienen claves distintas y ninguna sirve para la otra: un
              // pendiente sobre un publicado comparte `productId` con él, así que la clave
              // lleva prefijo para que no colisionen en la misma tabla.
              rowKey={(row: CatalogRow) =>
                esBorrador(row) ? `d-${row.id}` : row.objectID
              }
              // Filas compactas, las mismas que Atributos.
              size="small"
              loading={loading}
              dataSource={filas}
              columns={columnasVisibles}
              components={{ header: { cell: CatalogColumnHeaderCell } }}
              rowSelection={{
                selectedRowKeys: seleccion.map((r) =>
                  esBorrador(r) ? `d-${r.id}` : r.objectID,
                ),
                onChange: (_keys, rows) => setSeleccion(rows),
                // Lo elegido en otras páginas sigue elegido (xcons-docs#499): antd guarda esas
                // filas y las devuelve en `rows` aunque ya no estén en la tabla.
                preserveSelectedRowKeys: true,
                // **Sólo se marca lo que se puede operar** (xcons-docs#433). Lo decide el
                // API por fila (`bulkActions`, xcons-docs#430), que ya resolvió el nivel
                // propio sobre ese producto: un Admin por participación marca los suyos.
                getCheckboxProps: (row: CatalogRow) => ({
                  disabled: !puedeMarcarse(row, puedeEscribirTodo),
                }),
              }}
              // Alto fijo con scroll vertical, el mismo que Presupuestos, Pedidos y
              // Contactos: primero se scrollea la página, después se pagina.
              scroll={{ x: "max-content", y: 55 * 10.5 }}
              locale={{ emptyText: vacio() }}
              title={() => (
                <Flex vertical gap={16}>
                  <Flex gap={8}>
                    <Input.Search
                      allowClear
                      style={{ flex: 1 }}
                      // La búsqueda es asimétrica: en los publicados la resuelve Algolia
                      // y tolera typos; en los no publicados es un LIKE sobre las
                      // columnas desnormalizadas. Es el costo aceptado de no indexar
                      // los borradores.
                      placeholder="Buscar por SKU, nombre o marca"
                      defaultValue={filters.search}
                      onSearch={(search) => filtrar({ search })}
                    />
                    {/* El propio Estado vive adentro del panel (xcons-docs#366, punto
                        10): las facetas de Marca/Conjunto se esconden ahí mismo
                        cuando el estado elegido es Borrador/En revisión, en vez de
                        deshabilitar todo el botón. */}
                    <CatalogFiltersButton
                      filters={filters}
                      applyFilters={filtrar}
                      facetValues={facetValues}
                    />
                    {puedeEscribirTodo && (
                      <Button
                        icon={<UploadOutlined />}
                        onClick={() => navigate("/catalog/import")}
                      >
                        Importar
                      </Button>
                    )}
                    <Tooltip
                      title={
                        seleccion.length
                          ? "Exporta lo seleccionado, en el formato del importador"
                          : "Exporta todo lo que filtra el listado, en el formato del importador"
                      }
                    >
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={exportar}
                        loading={exportando}
                      >
                        Exportar
                      </Button>
                    </Tooltip>
                    {puedeCrear && (
                      <Dropdown
                        disabled={!conjuntos.length}
                        menu={{
                          items: conjuntos.map((c) => ({
                            key: String(c.attributeSetId),
                            label: etiquetaConjunto(c.name),
                          })),
                          onClick: ({ key }) =>
                            navigate(`/catalog/nuevo/edit?set=${key}`),
                        }}
                      >
                        <Button type="primary">
                          <Space>
                            Nuevo producto
                            <PlusOutlined />
                          </Space>
                        </Button>
                      </Dropdown>
                    )}
                  </Flex>

                  {/* Los filtros aplicados, con su cruz y Borrar filtros (xcons-docs#442). */}
                  <CatalogFilterTags filters={filters} applyFilters={filtrar} />

                  {/* Mismo lugar y formato que Contactos/Presupuestos/Pedidos: debajo de
                  la búsqueda, en texto secundario. */}
                  {total > 0 && (
                    <Text style={{ color: token.colorTextSecondary }}>
                      {total} productos
                    </Text>
                  )}

                  {error && <Alert type="error" showIcon message={error} />}

                  <BulkActions
                    seleccion={seleccion}
                    onLimpiar={() => setSeleccion([])}
                    onListo={() => filtrar({})}
                  />
                </Flex>
              )}
              // Sin `onChange` a propósito: ese handler disparaba en CUALQUIER cambio de
              // la tabla, paginación incluida, y llamar `applyFilters` ahí pisaba el
              // `setPage` de `pagination.onChange` en el mismo evento — era la causa de
              // que "siguiente" volviera sola a la página 1. Ya no hay column filter ni
              // selección que sincronizar acá (xcons-docs#366), así que no hace falta.
              onRow={(row: CatalogRow) => ({
                style: { cursor: "pointer" },
                onClick: (e) =>
                  // Marcar el checkbox no abre el producto: el click sube hasta la fila.
                  !(e.target as HTMLElement).closest(".ant-table-selection-column") &&
                  // Un borrador o una pendiente no abren la ficha del publicado: por SKU un
                  // alta ni existe en Magento, y en una edición se vería la versión vieja.
                  // **Un borrador abre ya editable** (xcons-docs#432): es trabajo a medias y
                  // lo que se hace con él es seguir cargándolo. Lo que está en revisión abre
                  // su revisión, que es donde se aprueba.
                  navigate(
                    esBorrador(row)
                      ? row.state === "draft"
                        ? `/catalog/pending/${row.id}/edit`
                        : `/catalog/pending/${row.id}`
                      : `/catalog/${encodeURIComponent(row.sku)}`,
                  ),
              })}
              pagination={{
                // `size="small"` de la tabla achica también el paginador; se deja el normal,
                // igual que en Atributos.
                size: "default",
                current: page,
                pageSize,
                // **Paginan los publicados.** Los no publicados van enteros en la primera
                // página: son cientos, salen de SQL, y su fuente no sabe paginar como el
                // índice. Sumarlos al total haría que la última página quedara corta.
                // **Topeado en lo que el índice pagina** (xcons-docs#517): con el `nbHits`
                // entero ofrecía páginas que llegan vacías.
                // Las pendientes y los borradores se paginan en memoria: el tope del índice no
                // les corresponde, y con él no se llegaba a la que está en el puesto 1.001.
                total: soloSinPublicar ? total : totalPaginable(total),
                showSizeChanger: true,
                pageSizeOptions: OPCIONES_POR_PAGINA,
                // En español, con las mismas claves que Presupuestos y Contactos: antd trae
                // los textos del paginador en inglés.
                locale: {
                  items_per_page: t("table.itemsPerPage"),
                  prev_page: t("table.prevPage"),
                  next_page: t("table.nextPage"),
                  jump_to: t("table.jumpTo"),
                  jump_to_confirm: t("table.jumpToConfirm"),
                  page: t("table.page"),
                  next_5: t("table.next5"),
                  prev_5: t("table.prev5"),
                },
                onChange: (p, size) => {
                  setPage(p);
                  if (size !== pageSize) setPageSize(size);
                },
                // El total que se muestra es el real, no el topeado que recibe `showTotal`. Sin
                // el aviso del tope ni la cuenta de sin publicar (xcons-docs#587).
                showTotal: () => (
                  <PaginationTotal total={total} entityName="productos" />
                ),
              }}
            />
          </div>
        </SortableContext>
      </DndContext>
    </>
  );
};
